<template>
  <div class="animated fadeIn">
    <div class="container">
      <form @submit.prevent="submit" novalidate="true">
        <b-modal
          ref="modalUnsavedChanges"
          :title="$t('Webportal.Dialog.UnsavedChanges.Title')"
          :ok-title="$t('Webportal.Dialog.UnsavedChanges.Ok')"
          :cancel-title="$t('Webportal.Dialog.UnsavedChanges.Cancel')"
          no-close-on-esc
          no-close-on-backdrop
          hide-header-close
        >
          {{ $t("Webportal.Dialog.UnsavedChanges.Text") }}
        </b-modal>

        <b-card no-body v-bind:class="{ spinner: isBusy }">
          <b-tabs pills card>
            <b-tab :title="$t('Webportal.Actions.SurveyRevision')">
              <div v-if="checkRevisionNotification">
                <b-form-group
                  :label-cols-sm="2"
                  :label="$t('Webportal.Notification')"
                  label-class="font-weight-bold pt-0"
                  :disabled="isBusy"
                >
                  <b-form-checkbox
                    v-model="checkRevisionNotification.NotifyOnNew"
                    v-validate="'required'"
                    data-vv-name="NotifyOnNewCheckRevisions"
                  >
                    {{
                      $t(
                        "Webportal.NotificationSettings.NotifyOnNewCheckRevisions"
                      )
                    }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="checkRevisionNotification.NotifyOnModified"
                    v-validate="'required'"
                    data-vv-name="NotifyOnModifiedCheckRevisions"
                  >
                    {{
                      $t(
                        "Webportal.NotificationSettings.NotifyOnModifiedCheckRevisions"
                      )
                    }}
                  </b-form-checkbox>
                </b-form-group>

                <br />

                <b-form-group
                  :label-cols-sm="2"
                  :label="$t('Webportal.Schedule')"
                  label-class="font-weight-bold pt-0"
                >
                  <schedule-input
                    v-model="checkRevisionNotification.CronScheduleUtc"
                    v-validate="'required'"
                    data-vv-name="CheckRevisionCronSchedule"
                    :disabled="
                      isBusy ||
                      (!checkRevisionNotification.NotifyOnNew &&
                        !checkRevisionNotification.NotifyOnModified)
                    "
                  />
                </b-form-group>
              </div>
              <span v-else-if="!isBusy">{{ $t("Webportal.NoDataFoundPlain") }}</span>
            </b-tab>

            <b-tab v-if="hasSurveyImprovement()" :title="$t('Webportal.Actions.SurveyImprovement')">
              <div v-if="checkImprovementNotification">
                <b-form-group
                  :label-cols-sm="2"
                  :label="$t('Webportal.Notification')"
                  label-class="font-weight-bold pt-0"
                  :disabled="isBusy"
                >
                  <b-form-checkbox
                    v-model="checkImprovementNotification.NotifyOnActionsToTake"
                    v-validate="'required'"
                    data-vv-name="NotifyOnActionsToTakeCheckImprovements"
                  >
                    {{
                      $t(
                        "Webportal.NotificationSettings.NotifyOnActionsToTakeCheckImprovements"
                      )
                    }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="checkImprovementNotification.NotifyOnNew"
                    v-validate="'required'"
                    data-vv-name="NotifyOnNewCheckImprovements"
                  >
                    {{
                      $t(
                        "Webportal.NotificationSettings.NotifyOnNewCheckImprovements"
                      )
                    }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="checkImprovementNotification.NotifyOnModified"
                    v-validate="'required'"
                    data-vv-name="NotifyOnModifiedCheckImprovements"
                  >
                    {{
                      $t(
                        "Webportal.NotificationSettings.NotifyOnModifiedCheckImprovements"
                      )
                    }}
                  </b-form-checkbox>
                </b-form-group>

                <br />

                <b-form-group
                  :label-cols-sm="2"
                  :label="$t('Webportal.Schedule')"
                  label-class="font-weight-bold pt-0"
                >
                  <schedule-input
                    v-model="checkImprovementNotification.CronScheduleUtc"
                    v-validate="'required'"
                    data-vv-name="CheckImprovementCronSchedule"
                    :disabled="
                      isBusy ||
                      (!checkImprovementNotification.NotifyOnActionsToTake &&
                       !checkImprovementNotification.NotifyOnNew &&
                       !checkImprovementNotification.NotifyOnModified)
                    "
                  />
                </b-form-group>
              </div>
              <span v-else-if="!isBusy">{{ $t("Webportal.NoDataFoundPlain") }}</span>
            </b-tab>
          </b-tabs>

          <template slot="footer">
            <div class="float-right">
              <button
                @click.prevent="cancel"
                class="btn btn-secondary"
                :disabled="!isFormChanged || isBusy"
              >
                <font-awesome-icon :icon="['far', 'ban']" />
                {{ $t("Webportal.Cancel") }}
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="!isFormChanged || !isFormValid || isBusy"
              >
                <font-awesome-icon :icon="['far', 'dot-circle']" />
                {{ $t("Webportal.Save") }}
              </button>
            </div>
          </template>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import ScheduleInput from "@/components/ScheduleInput";
import UsersService from "@/services/UsersService";

export default {
  name: "ExternalNotificationSettings",

  mixins: [AppConfig],

  components: {
    ScheduleInput,
  },

  data() {
    return {
      checkRevisionNotification: null,
      checkImprovementNotification: null,

      isBusy: false,
    };
  },

  computed: {
    isFormChanged() {
      return Object.keys(this.validationFields).some(
        (key) =>
          this.validationFields[key].dirty || this.validationFields[key].changed
      );
    },

    isFormValid() {
      return Object.keys(this.validationFields).every(
        (key) =>
          this.validationFields[key].valid === null ||
          this.validationFields[key].valid
      );
    },

    notificationData() {
      return [
        {
          Name: "Check Revisions",
          NotificationTypeId: 1, // Mail
          CronScheduleUtc: this.checkRevisionNotification.CronScheduleUtc,
          Subscription: {
            WorkflowEventIds: [
              ...(this.checkRevisionNotification.NotifyOnNew ? [11] : []),
              ...(this.checkRevisionNotification.NotifyOnModified ? [12] : []),
            ],
          },
        },
        {
          Name: "Check Improvements",
          IsDefault: this.checkImprovementNotification.IsDefault,
          NotificationTypeId: 1, // Mail
          CronScheduleUtc: this.checkImprovementNotification.CronScheduleUtc,
          Subscription: {
            WorkflowEventIds: [
              ...(this.checkImprovementNotification.NotifyOnActionsToTake ? [20] : []),
              ...(this.checkImprovementNotification.NotifyOnNew ? [21] : []),
              ...(this.checkImprovementNotification.NotifyOnModified ? [22] : []),
            ],
          },
        }
      ];
    },
  },

  mounted() {
    this.getNotificationSettings();
  },

  beforeRouteLeave(to, from, next) {
    this.componentUnsavedChanges(this.isFormChanged, next);
  },

  methods: {
    getNotificationSettings() {
      this.isBusy = true;

      UsersService.getUserProfile()
        .then((response) => {
          this.isBusy = false;

          const notificationData =
            response && response.data ? response.data.Notifications : null;

          let notification = notificationData?.find(
            (nd) => nd.Name === "Check Revisions"
          );
          this.checkRevisionNotification = {
            NotifyOnNew: notification?.Subscription.WorkflowEventIds.includes(11) ?? false,
            NotifyOnModified: notification?.Subscription.WorkflowEventIds.includes(12) ?? false,
            CronScheduleUtc: notification?.CronScheduleUtc,
          };

          notification = notificationData?.find(
            (nd) => nd.Name === "Check Improvements"
          );
          this.checkImprovementNotification = {
            NotifyOnActionsToTake: notification?.Subscription.WorkflowEventIds.includes(20) ?? false,
            NotifyOnNew: notification?.Subscription.WorkflowEventIds.includes(21) ?? false,
            NotifyOnModified: notification?.Subscription.WorkflowEventIds.includes(22) ?? false,
            CronScheduleUtc: notification?.CronScheduleUtc,
          };

          this.$validator.reset();
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },

    componentUnsavedChanges(isFormDirty, callback) {
      if (isFormDirty) {
        let dialog = this.$refs.modalUnsavedChanges;

        dialog.$off();
        dialog.$on("ok", (_) => callback());
        dialog.$on("cancel", (_) => callback(false));
        dialog.show();
      } else callback();
    },

    cancel() {
      this.componentUnsavedChanges(this.isFormChanged, (result) => {
        if (result !== false) this.getNotificationSettings();
      });
    },

    submit() {
      this.isBusy = true;

      UsersService.setUserProfile({ Notifications: this.notificationData })
        .then((response) => {
          this.$validator.reset();
        })
        .finally((_) => {
          this.isBusy = false;
        });
    },
  },
};
</script>
<style scoped>
/* Workaround: Overwrite base style in _nav.scss */
.tabs >>> .tab-content {
  border: 0;
}
</style>